import { withChartDefaults } from './documentExport';
import { drawAgencyHeader } from './agencyInfo';
import { fillablePdf } from './fillable';

const NAVY = [31, 56, 100];
const BLUE = [46, 109, 180];
const SLATE = [74, 85, 104];
const GRID = [210, 214, 220];
const BOTTOM = 255;
const EMPTY = /^(not available.*|n\/a|none provided|unknown)$/i;

const clean = (v) => {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'object' ? Object.values(v).filter(Boolean).join(' ') : String(v).trim();
  return EMPTY.test(s) ? '' : s;
};

const toRows = (value, keys) => {
  let list = value;
  if (typeof list === 'string') {
    try { list = JSON.parse(list); } catch { list = clean(list) ? list.split(/\n|;/) : []; }
  }
  if (!Array.isArray(list)) return [];
  return list.filter(Boolean).map(item => (typeof item === 'object'
    ? keys.map(k => clean(item[k]))
    : [clean(item), ...keys.slice(1).map(() => '')]));
};

const allergyText = (a) => {
  if (Array.isArray(a)) {
    return a.map(x => (typeof x === 'object' ? [x.allergen || x.name, x.reaction].filter(v => clean(v)).join(' - ') : x)).join(', ');
  }
  return clean(a);
};

const drawField = (doc, label, value, x, y, labelW, lineW) => {
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(...NAVY);
  doc.text(doc.splitTextToSize(label, labelW - 4), x, y);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 30, 30);
  const lines = doc.splitTextToSize(value || '', lineW);
  doc.addFill({ type: 'text', x: x + labelW, y: y - 3.6, w: lineW, h: 5.4 + (Math.min(lines.length, 2) - 1) * 4, value: value || '', multiline: lines.length > 1, fontSize: 9 });
  doc.setDrawColor(120, 120, 120);
  doc.line(x + labelW, y + 2 + (Math.min(lines.length, 2) - 1) * 4, x + labelW + lineW, y + 2 + (Math.min(lines.length, 2) - 1) * 4);
};

const drawTable = (doc, { title, headers, widths, rows, headColor, example, startY, minRows }) => {
  const left = 15;
  let y = startY;
  const total = widths.reduce((a, b) => a + b, 0);
  let x = left;
  const drawHeader = () => {
    doc.setFillColor(...headColor);
    doc.rect(left, y, total, 12, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(255, 255, 255);
    x = left;
    headers.forEach((h, i) => {
      doc.text(doc.splitTextToSize(h, widths[i] - 3), x + widths[i] / 2, y + 4.5, { align: 'center' });
      x += widths[i];
    });
    y += 12;
    doc.setDrawColor(...GRID);
  };
  if (y + 30 > BOTTOM) { doc.addPage(); y = 20; }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...BLUE);
  doc.text(title, left, y);
  y += 4;
  drawHeader();
  const all = [{ cells: example, italic: true }, ...rows.map(r => ({ cells: r }))];
  while (all.length < minRows + 1) all.push({ cells: headers.map(() => '') });
  doc.setDrawColor(...GRID);
  all.forEach(({ cells, italic }) => {
    doc.setFont('helvetica', italic ? 'italic' : 'normal'); doc.setFontSize(7.5);
    doc.setTextColor(...(italic ? [110, 110, 110] : [25, 25, 25]));
    const wrapped = cells.map((c, i) => doc.splitTextToSize(c || '', widths[i] - 3));
    const h = Math.max(6, ...wrapped.map(w => w.length * 3.4 + 3));
    if (y + h > BOTTOM) { doc.addPage(); y = 20; drawHeader(); }
    doc.setFont('helvetica', italic ? 'italic' : 'normal'); doc.setFontSize(7.5);
    doc.setTextColor(...(italic ? [110, 110, 110] : [25, 25, 25]));
    if (italic) { doc.setFillColor(246, 248, 251); doc.rect(left, y, total, h, 'F'); }
    x = left;
    wrapped.forEach((w, i) => {
      doc.rect(x, y, widths[i], h);
      if (italic) { if (w.length) doc.text(w, x + 1.5, y + 4); } else doc.addFill({ type: 'text', x: x + 0.5, y: y + 0.5, w: widths[i] - 1, h: h - 1, value: cells[i] || '', multiline: w.length > 1, fontSize: 8 });
      x += widths[i];
    });
    y += h;
  });
  return y;
};

export const buildMedicationListPdf = (rawData = {}, patientContext = {}) => {
  const c = patientContext.chart || {};
  const data = withChartDefaults(rawData, {
    patientName: patientContext.patientName, dateOfBirth: c.dateOfBirth || patientContext.dateOfBirth,
    allergies: Array.isArray(c.allergies) ? c.allergies.join(', ') : c.allergies,
    primaryPhysician: c.primaryPhysician, physicianPhone: c.primaryPhysicianPhone,
    pharmacyName: c.pharmacyName || c.preferredPharmacy, pharmacyPhone: c.pharmacyPhone,
  });
  const doc = fillablePdf({ unit: 'mm', format: 'letter' });
  doc.patientInfo = { name: data.patientName || patientContext.patientName, dob: data.dateOfBirth, mrn: patientContext.mrn || c.medicalRecordNumber, dx: c.primaryDiagnosis || patientContext.primaryDiagnosis, admission: c.startOfCareDate || patientContext.startOfCareDate };
  drawAgencyHeader(doc, 15, 6, { size: 15, rule: false });
  doc.autoFill = false;
  doc.setDrawColor(...NAVY); doc.line(15, 23, 201, 23);
  doc.autoFill = true;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(...NAVY);
  doc.text('Personal Medication List', 15, 32);
  doc.setFont('helvetica', 'italic'); doc.setFontSize(9); doc.setTextColor(90, 90, 90);
  doc.text('Keep this list updated and carry it with you to all medical appointments.', 15, 38);

  const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  const f = (k) => clean(data[k]);
  const fields = [
    ['Patient Name:', f('patientName') || clean(patientContext.patientName), 'Date of Birth:', f('dateOfBirth') || clean(patientContext.dateOfBirth)],
    ['Allergies/Reactions:', allergyText(data.allergies), 'Last Updated Date:', today],
    ['Primary Care MD:', f('primaryPhysician'), 'MD Phone Number:', f('physicianPhone') || f('primaryPhysicianPhone')],
    ['Preferred Pharmacy:', f('pharmacyName'), 'Pharmacy Phone:', f('pharmacyPhone')],
  ];
  let y = 49;
  fields.forEach(([l1, v1, l2, v2]) => {
    drawField(doc, l1, v1, 15, y, 38, 54);
    drawField(doc, l2, v2, 112, y, 36, 54);
    y += 13;
  });

  const routine = [
    ...toRows(data.routineMedications, ['medicationName', 'dosageStrength', 'route', 'timeFrequency', 'usedFor', 'notes']),
    ...toRows(data.otcSupplements, ['medicationName', 'dosageStrength', 'route', 'timeFrequency', 'usedFor', 'notes']),
  ];
  y = drawTable(doc, {
    title: 'Current Routine Medications (Prescriptions, OTC, Vitamins, Supplements)',
    headers: ['Medication Name (Brand & Generic)', 'Dosage / Strength', 'Route (Oral, etc.)', 'Time & Frequency', 'Used For (Condition)', 'Notes / Side Effects / Food Instructions'],
    widths: [33, 20, 20, 30, 30, 33],
    rows: routine,
    headColor: BLUE,
    example: ['Example: Lipitor (Atorvastatin)', '10 mg', 'Oral (By mouth)', '8:00 AM - Once Daily', 'High Cholesterol', 'Take in evening. Avoid grapefruit juice.'],
    startY: y + 6,
    minRows: 7,
  });

  y = drawTable(doc, {
    title: 'Short-Term or "As Needed" (PRN) Medications',
    headers: ['Medication Name', 'Dosage', 'Route', 'Max Dose / 24hr', 'Used For (Symptoms)', 'Special Instructions'],
    widths: [33, 20, 20, 30, 30, 33],
    rows: toRows(data.prnMedications, ['medicationName', 'dosage', 'route', 'maxDose24hr', 'usedFor', 'specialInstructions']),
    headColor: SLATE,
    example: ['Example: Tylenol (Acetaminophen)', '500 mg', 'Oral', 'Max 4 tabs (2000mg)', 'Headache / mild pain', 'Do not take with other acetaminophen products.'],
    startY: y + 12,
    minRows: 4,
  });

  if (y + 10 > BOTTOM + 8) { doc.addPage(); y = 20; }
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(130, 130, 130);
  doc.text('This is for informational purposes only. For medical advice or diagnosis, consult a professional. AI responses may include mistakes.',
    doc.internal.pageSize.getWidth() / 2, y + 8, { align: 'center' });
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7); doc.setTextColor(130, 130, 130);
    doc.text(`Page ${i} of ${pages}`, doc.internal.pageSize.getWidth() - 15, 272, { align: 'right' });
  }
  return doc;
};
